import { createContext, useCallback, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  enterFactoryContext,
  exitFactoryContext,
  getSessionIntent,
  isApprovedFactoryWorker,
  markPreferFactoryLogin,
  type SessionIntent,
} from '../lib/factoryContext';
import { useAuth } from './AuthContext';

export const FACTORY_LOGIN_PATH = '/login?intent=factory';

type FactoryContextValue = {
  intent: SessionIntent;
  /** True only while actually inside `/factory/*` with factory intent — not a sticky flag on learning shell. */
  inFactory: boolean;
  canEnterFactory: boolean;
  enterFactory: () => void;
  clearFactoryIntent: () => void;
  /** Leave factory Context, sign out, then land on factory login (not learning shell). */
  exitFactory: (redirectTo?: string) => Promise<void>;
};

const FactoryWorkspaceContext = createContext<FactoryContextValue | null>(null);

export function FactoryWorkspaceProvider({ children }: PropsWithChildren) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [intent, setIntent] = useState<SessionIntent>(() => getSessionIntent());

  const onFactoryRoute = location.pathname === '/factory' || location.pathname.startsWith('/factory/');
  const canEnterFactory = isApprovedFactoryWorker(user);

  const clearFactoryIntent = useCallback(() => {
    exitFactoryContext();
    setIntent('personal');
  }, []);

  // Stale factory intent must not leak into learning shell (e.g. denied gate → 返回学习壳).
  useEffect(() => {
    if (!onFactoryRoute && intent === 'factory') {
      clearFactoryIntent();
    }
  }, [clearFactoryIntent, intent, onFactoryRoute]);

  const enterFactory = useCallback(() => {
    if (!isApprovedFactoryWorker(user)) return;
    enterFactoryContext();
    setIntent('factory');
  }, [user]);

  const exitFactory = useCallback(
    async (redirectTo = FACTORY_LOGIN_PATH) => {
      // Mark before signOut: ProtectedRoute races to /login and must keep factory intent.
      markPreferFactoryLogin();
      clearFactoryIntent();
      try {
        await signOut();
      } catch {
        /* still leave the factory UI */
      }
      navigate(redirectTo, { replace: true });
    },
    [clearFactoryIntent, navigate, signOut],
  );

  const value = useMemo<FactoryContextValue>(
    () => ({
      intent,
      inFactory: intent === 'factory' && onFactoryRoute,
      canEnterFactory,
      enterFactory,
      clearFactoryIntent,
      exitFactory,
    }),
    [canEnterFactory, clearFactoryIntent, enterFactory, exitFactory, intent, onFactoryRoute],
  );

  return (
    <FactoryWorkspaceContext.Provider value={value}>{children}</FactoryWorkspaceContext.Provider>
  );
}

export function useFactoryWorkspace() {
  const value = useContext(FactoryWorkspaceContext);
  if (!value) throw new Error('useFactoryWorkspace must be used inside FactoryWorkspaceProvider');
  return value;
}
